import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from '@phosphor-icons/react';
import type { PageHeaderAction } from '@/components/core';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { LeaveRequestFormDialog } from '../my-time-off/components/leave-request-form-dialog/leave-request-form-dialog';
import { ToilRequestFormDialog } from '../my-toil/components/toil-request-form-dialog/toil-request-form-dialog';

/**
 * Request Time Off and Request TOIL, each for whoever may request it. Offered on My Time Off only (agreed
 * 2 Nov 2026: not on Sick Leave, Calendar, Team Time Off or Home). Spread `actions` into the page's
 * `PageHeader` and render `dialogs` once in the page.
 */
export const useTimeOffRequestActions = () => {
  const { t } = useTranslation();
  const canRequestLeave = useHasPermission(HRM_PERMISSIONS.TIME_OFF.LEAVE_REQUEST);
  const canRequestToil = useHasPermission(HRM_PERMISSIONS.TIME_OFF.TOIL_REQUEST);
  const [open, setOpen] = useState<'leave' | 'toil' | null>(null);

  const actions: PageHeaderAction[] = [
    ...(canRequestLeave
      ? [
          {
            label: t('REQUEST_TIME_OFF'),
            icon: <Plus className="size-5" />,
            onClick: () => setOpen('leave'),
          },
        ]
      : []),
    ...(canRequestToil
      ? [
          {
            label: t('REQUEST_TOIL'),
            icon: <Plus className="size-5" />,
            onClick: () => setOpen('toil'),
            variant: 'outline' as const,
          },
        ]
      : []),
  ];

  const dialogs = (
    <>
      {canRequestLeave && (
        <LeaveRequestFormDialog
          open={open === 'leave'}
          onOpenChange={(next) => setOpen(next ? 'leave' : null)}
        />
      )}
      {canRequestToil && (
        <ToilRequestFormDialog
          open={open === 'toil'}
          onOpenChange={(next) => setOpen(next ? 'toil' : null)}
        />
      )}
    </>
  );

  return { actions, dialogs };
};
